import React, { FC, useCallback, useContext, useEffect } from 'react';
import { apiOverview } from '../resources/api/apiOverview';
import { createFetcher } from '../contexts/Fetcher';
import Grid from '@mui/material/Grid';
import { NextExpiration } from '../components/AdminOverview/NextExpiration';
import Typography from '@mui/material/Typography';
import { Date } from '../components/Date/Date';
import { DateFormat } from '../helpers/dateFormat';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProductsFetcher } from '../fetchers/ProductsFetcher';
import { apiProduct } from '../resources/api/apiProduct';
import { useHistory, useParams } from 'react-router-dom';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { ActiveProductContext } from '../contexts/ActiveProductContext';
import { ProductSummaryFetcher } from '../fetchers/ProductSummaryFetcher';
import { ProductOverviewLatestVersion, ProductOverviewReleases } from '../components/UserOverview/ProductOverview';
import Box from '@mui/system/Box';
import { Nothing } from '../components/Nothing/Nothing';

const { useGetSummaryExpirations } = apiOverview;

const SummaryExpirationsFetcher = createFetcher(() => useGetSummaryExpirations().execute);

const ExpirationPaper = styled(Paper)({
  overflow: 'hidden',
});
const ExpirationHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
}));
const ExpirationWrapper = styled('div')(({ theme }) => ({
  maxHeight: 300,
  overflow: 'auto',
  padding: theme.spacing(3),
}));

const Expirations: FC = () => {
  const { t } = useTranslation();
  const { data } = useContext(SummaryExpirationsFetcher.Context);

  if (!data || data.length === 0) return null;

  return (
    <Grid item xs={12}>
      <ExpirationPaper>
        <ExpirationHeader>
          <Typography variant="h4">{t('adminOverview.nextExpiration')}</Typography>
          <Typography variant="h6">
            <Date date={data[0].expiresAt} format={DateFormat.Relative} />
          </Typography>
        </ExpirationHeader>
        <ExpirationWrapper>
          <NextExpiration licensesExpiring={data} />
        </ExpirationWrapper>
      </ExpirationPaper>
    </Grid>
  );
};

const OverviewSummary: FC = () => {
  const { data } = useContext(ProductSummaryFetcher.Context);
  const product = useContext(ActiveProductContext);
  const { t } = useTranslation();

  if (!data || !product) return null;

  if (data.installer.length === 0) {
    return (
      <Grid item xs={12}>
        <Nothing
          title={t('overview.nothing.title')}
          description={t('overview.nothing.description')}
          showContactAdmin={false}
        />
      </Grid>
    );
  }

  return (
    <>
      <ProductOverviewLatestVersion product={product} latest={data} xs={12} lg={6} />
      <ProductOverviewReleases product={product} latest={data} xs={12} lg={6} />
    </>
  );
};

const ProductsOverviewComponent: FC = () => {
  const { data: products } = useContext(ProductsFetcher.Context);

  const { product: activeProductName } = useParams<{ product?: string }>();
  const history = useHistory();

  const activeProduct = products?.products.find(({ name }) => name === activeProductName);

  const setActiveTab = useCallback((tab, replaceState = false) => {
    if (replaceState) {
      history.replace(AuthenticatedRoutes.GlobalOverviewProduct(tab));
    } else {
      history.push(AuthenticatedRoutes.GlobalOverviewProduct(tab));
    }
  }, []);

  const onChange = useCallback((event, tab) => {
    setActiveTab(tab);
  }, []);

  useEffect(() => {
    if (!activeProductName && products?.products.length) {
      setActiveTab(products.products[0].name, true);
    }
  }, [products, activeProductName]);

  if (!products) {
    return null;
  }

  return (
    <Box mt={3}>
      {activeProductName ? (
        <Tabs
          value={activeProductName}
          indicatorColor="primary"
          textColor="primary"
          onChange={onChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {products!.products.map((overviewProduct) => {
            return <Tab key={overviewProduct.id} label={overviewProduct.name} value={overviewProduct.name} />;
          })}
        </Tabs>
      ) : null}

      {activeProduct?.legacyId != null ? (
        <ActiveProductContext.Provider value={{ ...activeProduct, id: activeProduct.legacyId }}>
          <ProductSummaryFetcher.WAF>
            <Grid container spacing={3}>
              <OverviewSummary />
            </Grid>
          </ProductSummaryFetcher.WAF>
        </ActiveProductContext.Provider>
      ) : null}
    </Box>
  );
};

const { useGetProducts } = apiProduct;

export const GlobalOverviewPage: FC = () => {
  const { Guard: GuardSummaryExpirations } = useGetSummaryExpirations();
  const { Guard: GuardProducts } = useGetProducts();

  return (
    <>
      <Grid container spacing={3}>
        <GuardSummaryExpirations>
          <SummaryExpirationsFetcher.WAF>
            <Expirations />
          </SummaryExpirationsFetcher.WAF>
        </GuardSummaryExpirations>
      </Grid>
      <GuardProducts>
        <ProductsFetcher.WAF>
          <ProductsOverviewComponent />
        </ProductsFetcher.WAF>
      </GuardProducts>
    </>
  );
};
